import { useRouter } from 'next/router';
import React, { useMemo } from 'react';
import { useAdverts } from 'src/contexts/advertisement';
import { Article } from 'src/services/articles';
import { imageUrlMaker } from 'src/utils/image-helpers';
import styled from 'styled-components';
import ArticleDetail from './ArticleDetail';
import ArticleList from '../List/ArticleList';
import PromotionBanner from '../../../components/Promotion';

interface ArticleDetailPageProps {
  article: Article;
  relatedArticles: Article[] | undefined;
}

export default function ArticleDetailPage({
  article,
  relatedArticles,
}: ArticleDetailPageProps) {
  const router = useRouter();
  const adverts = useAdverts();

  const ads = useMemo(() => {
    return adverts?.map(
      advertisement =>
        advertisement &&
        advertisement.image && (
          <PromotionBanner
            key={`ad-${advertisement.id}`}
            href={advertisement.url}
            imageUrl={imageUrlMaker({
              imageUrl: advertisement.image.url,
              cropType: 'squareCrop',
              crop: advertisement.squareCrop,
            })}
          />
        ),
    );
  }, [adverts]);

  if (router.isFallback) {
    return <div>Loading...</div>;
  }

  if (!article) {
    return null;
  }

  const [firstAd, secondAd, ...otherAds] = ads || [];

  return (
    <ArticleDetailPageCss>
      <ArticleDetailCss article={article} />
      <ArticleDetailExtraCss>
        {firstAd}
        {secondAd}
        {relatedArticles && !!relatedArticles.length && (
          <ArticleList title="Related Posts" articles={relatedArticles} />
        )}
        {otherAds}
      </ArticleDetailExtraCss>
    </ArticleDetailPageCss>
  );
}

const ArticleDetailPageCss = styled.div`
  display: flex;

  ${p => p.theme.breakpoints.maxLg} {
    flex-direction: column;
  }
`;

const ArticleDetailCss = styled(ArticleDetail)`
  flex: 1;
  min-width: 0;
  margin-bottom: ${p => p.theme.sizes.spacing4};

  ${p => p.theme.breakpoints.lg} {
    margin-right: ${p => p.theme.sizes.spacing4};
    margin-bottom: 0;
  }
`;

const ArticleDetailExtraCss = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;

  & > div,
  ul:not(:last-child) {
    margin-bottom: ${p => p.theme.sizes.spacing6} !important;
  }

  ${p => p.theme.breakpoints.lg} {
    width: 317px;
  }
`;
